import type { AnimationConfig } from '@game-platform/pack-animation';
import { apiDownload, apiRequest } from '../api';

// LootMachine engine is mounted at /loot_machine in the main app
// (collectibles/config/routes.rb: `mount ::LootMachine::Engine, at: '/loot_machine'`),
// so all operator_console endpoints live under this prefix.
const BASE = '/loot_machine/api/operator_console/v1';

export interface OperatorMe {
  operator: {
    id: number;
    code: string;
    name: string;
    status: 'active' | 'disabled' | string;
    default_template: 'classic' | 'modern' | 'sleek' | 'glass' | string;
    available_templates: string[];
    /** Owner-only: half of the live S2S credential pair, so /me omits it for
     *  team members. */
    api_key?: string;
    currency: string | null;
    currencies: string[];
    allow_pricing_overrides: boolean;
    allow_packs_view: boolean;
    // Self-service editing of the test-player registry. Optional because the
    // exclusion itself never depends on it: with the flag off the operator
    // still sees the list, only mutations answer 403.
    allow_test_player_registry?: boolean;
    // The Metrics (KPI overview) screen. Optional while the backend rolls out;
    // read it as `?? false` so an older /me keeps the page hidden.
    allow_metrics?: boolean;
    // Self-service pack builder (My Packs). Optional for the same reason:
    // an older /me keeps the page hidden.
    allow_pack_builder?: boolean;
    // The Pack Opening screen (opening mechanic per template). Optional for
    // the same reason.
    allow_animation_settings?: boolean;
    // Fixed RTP and win rate per volatility level on the Pricing tab; works on
    // top of allow_pricing_overrides. Optional for the same reason.
    allow_fixed_rates?: boolean;
    // Editing a platform pack's buckets from the Pricing tab. Optional for the
    // same reason.
    allow_pack_buckets?: boolean;
    // The Tracking Pixels screen (partner ad pixels in the game). Optional for
    // the same reason.
    allow_tracking_pixels?: boolean;
    // The Media Kit screen (original marketing artwork). Optional for the same
    // reason.
    allow_media_kit?: boolean;
    // The Shipping Promotions screen (operator-funded delivery prices).
    // Optional for the same reason.
    allow_shipping_promotions?: boolean;
  };
  // Named `owner` for history: it is whoever is signed in, owner or invited
  // team member. `permissions` is already resolved by the backend — an owner
  // comes back holding every section, so the UI never branches on the role to
  // decide what to render.
  owner: {
    id: number;
    email: string;
    allow_password_change: boolean;
    role?: ConsoleRole;
    permissions?: ConsoleSection[];
  };
}

/** Per-operator feature flags on /me that decide whether a page exists at
 *  all (the section permission then says whether this person may open it).
 *  All are optional on the payload, so an older backend hides the page. */
export type OperatorFlag =
  | 'allow_packs_view'
  | 'allow_metrics'
  | 'allow_pack_builder'
  | 'allow_animation_settings'
  | 'allow_tracking_pixels'
  | 'allow_media_kit'
  | 'allow_shipping_promotions';

/** Sections a console user can be granted, mirrored from the backend's
 *  LootMachine::ConsolePermissions registry. */
export type ConsoleSection =
  | 'packs'
  | 'offers'
  | 'categories'
  | 'themes'
  | 'links'
  | 'players'
  | 'shipping'
  | 'logs'
  | 'test_players'
  | 'metrics'
  | 'webhooks'
  | 'tracking'
  | 'pack_builder'
  | 'media_kit'
  // Owner-only, never grantable — listed so `can()` can be asked about them.
  | 'integration'
  | 'api_keys'
  | 'security'
  | 'team';

export type ConsoleRole = 'owner' | 'member';

export interface TeamMember {
  id: number;
  user_id: number;
  email: string;
  role: ConsoleRole;
  permissions: ConsoleSection[];
  two_factor_enrolled: boolean;
  must_change_password: boolean;
  last_seen_at: string | null;
  created_at: string;
}

export interface TeamResponse {
  members: TeamMember[];
  permissions: {
    sections: ConsoleSection[];
    owner_only: ConsoleSection[];
    presets: Record<string, ConsoleSection[]>;
  };
}

/** A URL of yours we notify, and what it asked to hear about. Separate from
 *  the wallet callbacks on the Integration page: those are the money protocol
 *  and we act on their answer, these only have to come back 2xx. */
export interface WebhookEndpoint {
  id: number;
  url: string;
  description: string | null;
  event_types: string[];
  status: 'active' | 'paused';
  /** Failures in a row. The backend pauses an endpoint once this gets high
   *  enough, rather than hammering a dead URL forever. */
  consecutive_failures: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  created_at: string;
}

/** One subscribable event, straight from the backend registry — the UI never
 *  hard-codes this list, so a new event shows up as a checkbox on its own. */
export interface WebhookEventInfo {
  type: string;
  description: string;
}

export interface WebhookEndpointsResponse {
  webhook_endpoints: WebhookEndpoint[];
  available_events: WebhookEventInfo[];
}

export type WebhookDeliveryStatus =
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'exhausted'
  /** The endpoint was paused when the event fired: recorded, not sent, and
   *  replayable one by one. */
  | 'skipped';

export interface WebhookDelivery {
  id: number;
  endpoint_id: number;
  event_id: string;
  event_type: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  max_attempts: number;
  last_response_status: number | null;
  last_error: string | null;
  occurred_at: string | null;
  delivered_at: string | null;
  created_at: string;
  /** Exactly the bytes we signed and sent, so you can replay the signature
   *  check against them. There is deliberately no response body. */
  payload: unknown;
}

export interface WebhookDeliveriesResponse {
  webhook_deliveries: WebhookDelivery[];
  meta: { total_count: number; page: number; per: number };
}

/** Send test / Resend hand back the delivery they queued; watch it with
 *  getWebhookDelivery until it leaves `pending`. Sending happens in a worker,
 *  not while the request is open — the URL belongs to the operator, and a host
 *  that never answers must not be able to hold a request thread. */
export interface WebhookAttemptResponse {
  webhook_delivery: WebhookDelivery;
}

export interface ListWebhookDeliveriesParams {
  page?: number;
  per?: number;
  endpoint_id?: number;
  event_type?: string;
  status?: WebhookDeliveryStatus;
}

export interface TeamMutationResponse {
  member: TeamMember;
  /** Shown once and never retrievable again — the backend stores only its
   *  hash. Every console account is created by the console itself (an email
   *  that already exists on collectibles.com is refused), so this is always
   *  present on a successful add. */
  temporary_password?: string | null;
}

export interface OperatorSession {
  id: number;
  operator_token: string;
  operator_account_id: string | null;
  template: string;
  created_at: string;
  expires_at: string;
  deleted_at: string | null;
  expired: boolean;
  player_username: string | null;
  player_country: string | null;
  player_currency: string | null;
}

/** KPI figures for the requested window. Every *_cents value is in the
 *  operator's own currency minor units. */
export interface MetricsPeriod {
  pack_sales_cents: number;
  /** A round is one awarded card: it closes once, and that single event is both
   *  the sale and the payout. `packs_sold` and `buyback_count` carry the same
   *  figures under their older names; nothing reads them any more, and both are
   *  OPTIONAL so that dropping them from the backend stays a backend change
   *  rather than a compile error over here. */
  rounds_count: number;
  packs_sold?: number;
  payouts_cents: number;
  buyback_count?: number;
  ggr_cents: number;
  operator_revenue_cents: number;
}

/** One point of the overview series. Carries only the switchable figures —
 *  payouts and buyback counts are window-level, not part of that block. */
export interface MetricsPoint {
  /** The day, or the first day of the month, depending on `granularity`. */
  bucket: string;
  pack_sales_cents: number;
  rounds_count: number;
  /** The older name for the same count; see MetricsPeriod. */
  packs_sold?: number;
  ggr_cents: number;
  operator_revenue_cents: number;
}

/** Step of the series: a short window is drawn day by day, a long one month by
 *  month. Decided by the backend from the window alone. */
export type MetricsGranularity = 'day' | 'month';

/** An advanced block the day rollups cannot answer for this window yet — a
 *  backfill or a nightly run away, NOT something polling will fix. Distinct
 *  from `null`, which does mean "still being prepared". */
export interface MetricsUnavailable {
  unavailable: string;
}

export interface MetricsOfferSlice {
  pool_offer_id: number;
  title: string | null;
  /** Built by the operator in the console, as opposed to one of ours. Null when
   *  the offer row is gone for good — `with_deleted` on the backend covers soft
   *  deletes only, so ownership is then unknown rather than false. */
  own_pack: boolean | null;
  count: number;
  /** 0..1 of the window's rips. */
  share: number;
}

export interface MetricsPriceSlice {
  /** null for the one slice of all custom pack cards, which have no pack price. */
  unit_price_cents: number | null;
  count: number;
  share: number;
}

/** Daily Rips: how many packs were opened in the window and how they split.
 *  On the AWARD clock — when the card was won — unlike the money above, which
 *  is settled per closed round. */
export interface MetricsRips {
  total: number;
  by_offer: MetricsOfferSlice[];
  by_price: MetricsPriceSlice[];
  new_buyers: number;
  /** What those new buyers were worth over the window. */
  lead_reward_cents: number;
  /** The operator's RATE per new buyer. Zero — the column default, so most
   *  operators — means we pay nothing for leads here, which is not the same as
   *  a period that simply had no new buyers. */
  lead_reward_rate_cents: number;
  /** These come off the nightly rollups plus a live tail, so they are a
   *  different freshness from `period` — the screen has to say which. Nullable:
   *  the serializer emits it through `&.iso8601`. */
  as_of: string | null;
}

export interface MetricsShare {
  count: number;
  share: number;
}

/** Rip Results: what players got, and what they did with it. */
export interface MetricsRipResults {
  fmv: {
    median_cents: number | null;
    average_cents: number | null;
    max_cents: number | null;
    sum_cents: number;
    cards: number;
  };
  /** Cards the player kept — a delivery. Keeping a card in the inventory is not
   *  an option: the operator's timer sells whatever was left alone. */
  held: MetricsShare;
  sold: MetricsShare & {
    by_source: {
      player_sell: number;
      partner_api_sell: number;
      auto_sell: number;
      /** A sale with no source recorded — zero in healthy data. */
      unknown: number;
    };
  };
  /** Still inside the timer window: a state, not a choice. */
  pending: MetricsShare;
  refunded_deliveries: number;
  admin_removed: number;
  /** Cards whose state the log cannot explain; zero in healthy data. */
  anomalies: number;
  /** 0 = the timer is off for this operator. Held/Sold are not comparable
   *  between operators without it. */
  auto_sell_after_hours: number;
  as_of: string | null;
}

/** Buyer-level figures. LTV is net and cumulative THROUGH THE WINDOW'S END, on
 *  the same basis as the settlement report: sales − buyback − redemption value,
 *  per buyer. Both sides of that ratio stop on the same day — the buyer count is
 *  bounded by the window too, not by today. */
export interface MetricsPlayers {
  buyers_count: number;
  /** Closed-round pack sales per buyer, cumulative through the period end. */
  ltv_gross_cents: number;
  /** The same, less buyback payouts and the value of delivered cards. */
  ltv_net_cents: number;
  lifetime_sales_cents: number;
  lifetime_ggr_cents: number;
  as_of: string | null;
}

/** Window head counts. Live aggregates, so this block has its own freshness and
 *  is served even when the day summaries behind the other blocks are behind. */
export interface MetricsPlayerActivity {
  /** Distinct accounts awarded a card in the period. */
  unique_players: number;
  cards: number;
  rips_per_player: number;
  /** Of those players, the ones whose first purchase predates the period. */
  recurring_count: number;
  recurring_share: number;
  new_buyer_count: number;
  new_buyer_share: number;
  /** The partner's name for the line, but it counts FIRST SESSIONS — we issue
   *  no registration of our own for a seamless operator, so this will not tie
   *  out against the operator's own registration count. */
  new_sign_ups: number;
  as_of: string | null;
}

/** One checkpoint of a cohort. Everything is null until the cohort has lived
 *  through the window — a partial figure is not a 90-day LTV. */
export type MetricsCohortGranularity = 'day' | 'week' | 'month';

/** One column of a cohort row: money accumulated through that many periods
 *  since each account's OWN first purchase, on both bases. */
export interface MetricsCohortCell {
  /** Which window this is, counted from zero: the retention convention, where
   *  0 is the first window after the purchase. The backend sends it rather than
   *  letting the client count, so a row with a missing tail cannot be
   *  misaligned. */
  offset: number;
  gross_cents: number;
  net_cents: number;
  ltv_gross_cents: number;
  ltv_net_cents: number;
  /** Accounts of this cohort that opened a pack from an order OTHER than the
   *  one that made them a buyer, during this column's window. */
  returned_accounts: number;
  /** returned_accounts over the cohort's size, 0..1. PER COLUMN, never
   *  cumulative — unlike the money above, which accumulates across columns. */
  retention: number;
}

export interface MetricsCohortRow {
  /** ISO date of the first day of the cohort period. */
  cohort: string;
  accounts: number;
  /** A PREFIX, never sparse: the cells stop at the newest column this cohort
   *  has lived through, which is what makes the table a triangle. A short row
   *  is a young cohort, not missing data. */
  cells: MetricsCohortCell[];
}

/** Cohort LTV as a triangle: a row per cohort period, a column per period of
 *  age. Not scoped to the selected period — a cohort's window runs from its own
 *  first purchase — so this block must never be captioned with the date
 *  picker's dates. */
/** How far back the rows reach. A number counts periods back from the newest
 *  reportable cohort; "last_week" and "last_month" name the CALENDAR period
 *  that closed, which is a different range from "the last 7 / 30 days" and is
 *  why the partner lists both. */
export type MetricsCohortTimeframe = number | 'last_week' | 'last_month';

export interface MetricsCohorts {
  granularity: MetricsCohortGranularity;
  timeframe: MetricsCohortTimeframe;
  /** What `timeframe` may be for this granularity. Sent by the backend so the
   *  selector can be drawn for a granularity whose payload has not arrived. */
  timeframes: MetricsCohortTimeframe[];
  /** Column labels, left to right. */
  offsets: number[];
  /** First day the card log covers for this operator; earlier cohorts can
   *  never be measured on this basis. */
  coverage_from: string | null;
  rows: MetricsCohortRow[];
  as_of: string | null;
}

export type MetricsBlock<T> = T | MetricsUnavailable | null;

export function isUnavailable<T>(block: MetricsBlock<T>): block is MetricsUnavailable {
  return block !== null && typeof block === 'object' && 'unavailable' in block;
}

export interface OperatorMetricsResponse {
  /** A block is still being aggregated in the background: both blocks below can
   *  be null, and the screen is expected to ask again shortly. */
  computing: boolean;
  from: string;
  to: string;
  currency: string | null;
  /** The day boundary the window and the series are cut on — the operator's own
   *  day may start elsewhere. */
  timezone?: string;
  /** What was counted: rounds closed inside the window, read off the outcome
   *  log, which begins on 2026-07-12. */
  coverage?: string | null;
  granularity?: MetricsGranularity;
  period: MetricsPeriod | null;
  /** Across the requested window, oldest first, empty buckets included. */
  series: MetricsPoint[] | null;
  /** The three advanced blocks. Absent entirely unless the operator has
   *  allow_advanced_metrics — an absent key means "no such feature here",
   *  a null means "being prepared", and { unavailable } means the rollups
   *  cannot answer this window yet. */
  rips?: MetricsBlock<MetricsRips>;
  rip_results?: MetricsBlock<MetricsRipResults>;
  players?: MetricsBlock<MetricsPlayers>;
  player_activity?: MetricsBlock<MetricsPlayerActivity>;
  cohorts?: MetricsBlock<MetricsCohorts>;
}

export interface GetMetricsParams {
  /** ISO dates, both inclusive. Omit both for the backend default (last 30
   *  days); a window longer than 366 days answers 422. */
  from?: string;
  to?: string;
  /** Step of the series. Omit to let the window decide (day up to a quarter,
   *  month past it) — which is what the screen does until the switch is used. */
  granularity?: MetricsGranularity;
  /** Shape of the cohort triangle, unrelated to `granularity` above. The
   *  backend caches per shape and never computes in-request, so asking for a
   *  shape returns null until its job lands — the same `computing` cycle as
   *  any other block. A value the backend does not offer answers 422 rather
   *  than being rounded to a neighbour. */
  cohort_granularity?: MetricsCohortGranularity;
  cohort_timeframe?: MetricsCohortTimeframe;
}

export interface PaginationMeta {
  total_count: number;
  page: number;
  per: number;
}

// ---------------------------------------------------------------------------
// Pack buckets: the shared ladder of a platform pack the operator sells.
// ---------------------------------------------------------------------------

/** One bucket of the ladder; `to: null` is open-ended. `only` (card rules) is
 *  support's to edit: the console sends it back untouched. */
export interface PackBucketRow {
  from: number;
  to: number | null;
  floor: number | null;
  only?: unknown;
}

export interface PackBucketsIssue {
  code: string;
  message: string;
  /** The volatility level for a refused own pricing. */
  field?: string | null;
}

/** One bucket of a dry run; `share` and `your_share` are 0-1 shares of pulls.
 *  `stocked` says whether the bucket holds any card: stock volumes stay with the platform. */
export interface PackBucketsBucket {
  range: string;
  stocked: boolean;
  avg_fmv: number | null;
  share: number | null;
  /** This operator's Normal share; null when they do not price the pack. */
  your_share: number | null;
  side: 'low' | 'high';
}

export interface PackBucketsCheck {
  ok: boolean;
  errors: PackBucketsIssue[];
  warnings: PackBucketsIssue[];
  buckets: PackBucketsBucket[];
  expected_value: number | null;
  relaxed_floors: boolean;
}

export interface PackBuckets {
  groups: PackBucketRow[];
  price: number;
  /** Another seller or storefront sells the pack too. */
  shared: boolean;
  editable: boolean;
  locked_reason: 'card_rules' | 'static_chances' | null;
  last_change: { at: string; by: 'you' | 'another_seller' | 'support' } | null;
}

// ---------------------------------------------------------------------------
// Pack Builder (My Packs). Types mirror
// LootMachine::Api::OperatorConsole::V1::PackBuilder::{Pack,Item,Limits}Serializer
// and PackBuilder::ValidationReport 1:1.
// ---------------------------------------------------------------------------

/** One bucket of the pack's odds ladder. `to: null` = open-ended ("100+").
 *  `floor` is the solver's minimum win probability for the bucket, part of the
 *  wire contract (GroupsCodec encodes a floored bucket as
 *  `{"bucket":"100..Infinity","floor":5}`) but NOT editable in the console: the
 *  client reads it, keeps it and PATCHes it back unchanged. */
export interface PackBuilderGroup {
  from: number;
  to: number | null;
  floor: number | null;
}

export interface PackBuilderImages {
  bg_image_url: string | null;
  pack_image_url: string | null;
  pack_logo_image_url: string | null;
  pedestal_image_url: string | null;
}

export type PackBuilderAssetSlot = 'bg_image' | 'pack_image' | 'pack_logo_image' | 'pedestal_image';

/** Result of the last validation the backend stored for the pack; null until
 *  the pack was validated once. */
export interface PackBuilderHealth {
  ok: boolean;
  errors_count: number;
  warnings_count: number;
  ev_margin: number | null;
  rtp: number | null;
  checked_at: string;
}

export interface PackBuilderLimits {
  max_rtp: number;
  min_rtp_warn: number;
  max_items: number;
  max_packs: number;
  max_items_per_request: number;
  /** What a pack pays back on a sell while it carries no override of its own
   *  (OperatorPoolOfferConfig::DEFAULT_BUYBACK_PERCENT), as a 0-1 share. */
  default_buyback_percent: number;
}

export interface PackBuilderPack {
  id: number;
  title: string;
  description: string | null;
  price: number;
  currency: string | null;
  stated_rtp: number;
  /** price × stated_rtp, maintained by the backend (PackSerializer emits
   *  `estimated_value&.to_f`, so a pack seeded outside the builder may be null). */
  estimated_value: number | null;
  status: 'draft' | 'available' | 'restocking' | string;
  /** Owner config availability. The enum has five values; the console can act
   *  on `staged` only — `enabled`/`restocking` are live and `disabled` is set
   *  by support. */
  availability: 'staged' | 'enabled' | 'restocking' | 'disabled' | 'default' | string;
  items_count: number;
  category_id: number | null;
  pool_category_ids: number[];
  card_grade_type: 'ungraded' | 'graded' | null;
  groups: PackBuilderGroup[];
  groups_custom: boolean;
  /** The pack's own buyback override as a 0-1 share; null means the platform
   *  default (`limits.default_buyback_percent`) applies. */
  buyback_percent: number | null;
  images: PackBuilderImages;
  badge_name: string | null;
  health: PackBuilderHealth | null;
  created_at: string;
  updated_at: string;
}

export interface PackBuilderCreateAttrs {
  title: string;
  description: string;
  price: number;
  stated_rtp: number;
  category_id?: number;
  pool_category_ids: number[];
  card_grade_type?: 'ungraded' | 'graded';
  badge_name?: string;
}

/** PATCH body. price / stated_rtp / groups / reset_groups answer 409
 *  PACK_PUBLISHED while the pack is live (enabled or restocking). */
export interface PackBuilderUpdatePatch {
  title?: string;
  description?: string;
  price?: number;
  stated_rtp?: number;
  category_id?: number | null;
  pool_category_ids?: number[];
  card_grade_type?: 'ungraded' | 'graded' | null;
  badge_name?: string;
  /** 0-1 share; null clears the override back to the platform default. Unlike
   *  price/RTP/groups this one is editable while the pack is live. */
  buyback_percent?: number | null;
  groups?: PackBuilderGroup[];
  /** "Reset Price Buckets" (CONTRACT §4.9): the backend regenerates
   *  GroupsTemplate.ladder(price) and sets groups_custom=false. Never sent
   *  together with `groups`. */
  reset_groups?: boolean;
}

export interface PackBuilderItem {
  id: number;
  title: string;
  image_url: string | null;
  fmv: number;
  grade: { company: string | null; grade: string | null } | null;
  sport: string | null;
  player: string | null;
  year: number | null;
  set: string | null;
  ownership: 'shared' | 'mine';
  in_pack: boolean;
  /** The pack bucket this card falls into ("25..50"); null when outside every bucket. */
  bucket: string | null;
  /** Pool-item lifecycle: listed / on_hands / buyback / redeemed / unavailable /
   *  reserved / burned. Sent by the pack-contents endpoint, where a member can
   *  be in any of them; the ES-backed inventory only ever holds `listed`. Kept
   *  as a string so a new lifecycle value cannot break the client. */
  engagement_status?: string | null;
}

export type PackBuilderSort = 'fmv_desc' | 'fmv_asc' | 'newest' | 'title';

export interface PackBuilderItemsResponse {
  items: PackBuilderItem[];
  meta: PaginationMeta;
}

/** `reason` is one of LinkPolicy::REASONS (different_pool, not_available,
 *  foreign_operator_item, not_eligible, grade_mismatch) or LinkItems' own
 *  not_found / already_in_pack /
 *  not_in_pack (CONTRACT §4.10). Kept as string: new reasons must not break
 *  the client. */
export interface PackBuilderSkipped {
  id: number;
  reason: string;
}

export interface PackValidationIssue {
  code: string;
  message: string;
  field?: string | null;
  ids?: number[] | null;
  level: 'error' | 'warning' | 'info';
}

export interface PackValidationBucket {
  range: string;
  count: number;
  avg_fmv: number | null;
  sum_fmv: number;
  share: number | null;
  side: 'low' | 'high';
}

export interface PackValidationOdds {
  display_chances: Record<string, number>;
  raw_chances: Record<string, number>;
  expected_value: number;
  /** Solver diagnostics (v2: ev_margin, achieved_win_rate, empty_buckets, …).
   *  Read defensively — keys differ between solver versions. */
  diagnostics: Record<string, unknown>;
}

export interface PackValidationEconomics {
  price: number;
  target_ev: number;
  rtp: number;
  house_edge: number;
  expected_payout: number;
  expected_ggr: number;
  operator_share: number;
  buyback_share_used: number;
  buyback_percent: number;
}

export interface PackValidation {
  ok: boolean;
  solver: 'v1' | 'v2' | string;
  errors: PackValidationIssue[];
  warnings: PackValidationIssue[];
  odds: PackValidationOdds | null;
  buckets: PackValidationBucket[];
  /** `{}` when a step-0 hard error (PRICE_INVALID, RTP_OUT_OF_RANGE, GROUPS_*)
   *  stops the validator before it prices anything — read every key as optional. */
  economics: Partial<PackValidationEconomics>;
}

export interface PackValidateOverrides {
  price?: number;
  stated_rtp?: number;
  groups?: PackBuilderGroup[];
  item_ids?: number[];
}

export interface AddItemsResponse {
  added: number[];
  skipped: PackBuilderSkipped[];
  validation: PackValidation;
}

export interface RemoveItemsResponse {
  removed: number[];
  /** Ids that were not in the pack (`not_in_pack`, CONTRACT §4.10). Optional
   *  so a backend that omits the key still types. */
  skipped?: PackBuilderSkipped[];
  validation: PackValidation;
}

export interface PackBuilderTemplateGroup {
  from: number;
  to: number | null;
}

/** GET pack_builder/pool_categories — every LootMachine::PoolCategory
 *  (CONTRACT §4.12), for the pack form. */
export interface PackBuilderPoolCategory {
  id: number;
  name: string;
}

export type InventoryOwnership = 'any' | 'mine' | 'shared';

/** The picker. There is no `in_pack` mode: with `pack_id` set the endpoint
 *  always excludes cards the pack already holds. Pack contents come from
 *  packs/:id/items (Postgres) — never from here. */
export interface InventoryParams {
  pack_id: number;
  q?: string;
  ownership?: InventoryOwnership;
  fmv_min?: number;
  fmv_max?: number;
  category_id?: number;
  sport?: Array<string | number>;
  grader?: Array<string | number>;
  grade?: Array<string | number>;
  year_from?: number;
  year_to?: number;
  set?: Array<string | number>;
  player?: string;
  rookie?: boolean;
  pokemon_rarity?: Array<string | number>;
  pokemon_set?: Array<string | number>;
  sort?: PackBuilderSort;
  page?: number;
  per?: number;
  group_variations?: boolean;
}

export interface InventoryFacet {
  id: string | number;
  label: string;
  count: number;
}

export type InventoryFacetKey =
  | 'category'
  | 'sport'
  | 'grader'
  | 'grade'
  | 'year'
  | 'set'
  | 'pokemon_rarity';

export type InventoryFacets = Partial<Record<InventoryFacetKey, InventoryFacet[]>>;

/** One bucket of the bounded-range FMV histogram (edges = price ×
 *  [0.25, 0.5, 1, 2, 5, 10, 25, 50, 100]; first `from` and last `to` are null). */
export interface InventoryFmvBucket {
  from: number | null;
  to: number | null;
  count: number;
}

export interface InventoryFmv {
  min: number | null;
  max: number | null;
  /** CONTRACT §4.27 — typed, but v1 does not render it. */
  histogram: InventoryFmvBucket[];
}

export interface InventoryPackBucket {
  range: string;
  count_matching: number;
}

export interface InventoryResponse {
  items: PackBuilderItem[];
  meta: PaginationMeta;
  facets: InventoryFacets;
  fmv: InventoryFmv;
  pack_buckets: InventoryPackBucket[];
}

export interface SessionsResponse {
  sessions: OperatorSession[];
  meta: PaginationMeta;
}

export interface OperatorTransaction {
  id: number;
  external_transaction_id: string | null;
  round_id: string | null;
  direction: string;
  status: string;
  amount_cents: number;
  amount_dollars: string;
  session_id: number | null;
  pool_order_id: number | null;
  created_at: string;
}

export interface TransactionsResponse {
  transactions: OperatorTransaction[];
  meta: PaginationMeta;
}

export interface OperatorSettings {
  callback_base_url: string | null;
  callback_paths: Record<string, string>;
  ip_whitelist: string[];
  default_template: 'classic' | 'modern' | 'sleek' | 'glass' | string;
  registration_mode: 'disabled' | 'required';
  embed_key: string | null;
}

export interface TemplateThemeMeta {
  template: string;
  has_overrides: boolean;
  updated_at: string | null;
}

export interface TemplateTheme {
  template: string;
  has_overrides: boolean;
  design_system: Record<string, unknown>;
  overrides: Record<string, unknown>;
  updated_at: string | null;
}

// One row of the read-only Packs tab: a pool offer enabled for this operator.
// `status` is the operator-facing effective status — disabled packs and drafts
// never reach the response; `restocking` covers both a globally out-of-stock
// pack and one auto-hidden for this operator.
export interface Pack {
  id: number;
  title: string;
  price: number;
  stock_count: number;
  status: 'available' | 'restocking' | string;
}

/** A pack the global defaults left at its RTP: its cards can't pay the new one, or the server names another reason. */
export interface RtpSkippedPack {
  pool_offer_id: number;
  title: string;
  message: string;
  /** RTP % range its cards can pay right now; null when none. */
  reachable_rtp: [number, number] | null;
  /** Fixed rates only: win rate % range its cards can hold right now; null when none. */
  reachable_win_rate?: [number, number] | null;
  /** Set when the pack was skipped for a reason other than its cards, e.g. 'fixed_rates_disabled'. */
  reason?: string;
}

/** 422 of a pricing PATCH: a volatility level the pack's current cards can't pay or hold. */
export interface RtpNotPayable {
  error: 'rtp_not_payable';
  message: string;
  level: 'normal' | 'high' | 'max';
  /** RTP % / win rate % ranges the cards can hold right now, rounded inward; null when none. */
  reachable_rtp: [number, number] | null;
  reachable_win_rate?: [number, number] | null;
}

export interface PoolOfferConfig {
  pool_offer_id: number;
  pool_offer_title: string;
  // Raw per-operator title override; null means "inherits the pool offer's
  // title" (pool_offer_title). Blank on PATCH clears it back to null.
  title: string | null;
  // Raw per-operator badge name override; null means "inherits the pool
  // offer's badge" (pool_offer_badge_name, null when the pack has none).
  badge_name: string | null;
  pool_offer_badge_name: string | null;
  // Raw per-operator description override; null means "inherits the pool
  // offer's own description" (pool_offer_description), which is always present.
  description: string | null;
  pool_offer_description: string | null;
  // Pack sticker price; the console derives RTP % = estimated value / price.
  pool_offer_price: number | null;
  pool_offer_estimated_value: number | null;
  // A custom pack is built by the player: it has no sticker price or estimated
  // value, so its RTP lives in stated_rtp (a 0-1 share) instead.
  pool_offer_game_kind: 'loot_pack' | 'custom_pack';
  pool_offer_stated_rtp: number | null;
  // 'staged' = prepared for this operator but NOT live: configurable here,
  // absent from the player catalog and unbuyable until published.
  availability: 'default' | 'disabled' | 'enabled' | 'staged' | 'restocking' | string;
  estimated_value: number | null;
  // Per-operator estimated value of the High / Max volatility level; null means
  // the level plays estimated_value (Normal). Loot packs only.
  estimated_value_high: number | null;
  estimated_value_max: number | null;
  // Fixed rates: every volatility level plays its own RTP and win rate (0-1
  // shares) instead of the estimated values above. Loot packs only; sent for
  // every pack, false / null until set.
  fixed_rates: boolean;
  rtp_normal: number | null;
  rtp_high: number | null;
  rtp_max: number | null;
  win_rate_normal: number | null;
  win_rate_high: number | null;
  win_rate_max: number | null;
  // Max plays only cards far below or far above the pack price.
  max_extremes: boolean;
  // Raw per-operator RTP override of a custom pack (0-1 share); null means
  // "inherits pool_offer_stated_rtp". Rejected by the backend for a loot pack.
  stated_rtp: number | null;
  marketplace_fee_rate: number | null;
  buyback_percent: number | null;
  // Free no-signup demo rip for this pack (players always get a good card).
  demo_mode: boolean;
  has_overrides: boolean;
  bg_image_url: string | null;
  pack_image_url: string | null;
  pack_logo_image_url: string | null;
  pedestal_image_url: string | null;
  // Artwork of a few random pullable cards of this pack, for previews that
  // need real cards. Empty when the pack has no pullable card with an image.
  sample_card_image_urls: string[];
  bg_image_override: boolean;
  pack_image_override: boolean;
  pack_logo_image_override: boolean;
  pedestal_image_override: boolean;
  // Pack's own opening, replaces the template's animation whole; null = global.
  animation: AnimationConfig | null;
}

export interface PoolCategoryConfig {
  pool_category_id: number;
  // Raw per-operator name override; null means "inherits the pool
  // category's name" (pool_category_name).
  name: string | null;
  pool_category_name: string;
  // Override image; null means the iframe falls back to
  // pool_category_image_url (the category's own image).
  image_url: string | null;
  pool_category_image_url: string | null;
  // Per-operator sort order (0..9999, lower shows first); null means the
  // category keeps the default order, after every ordered one.
  position: number | null;
  image_override: boolean;
  has_overrides: boolean;
}

// Semantic role of a footer link: 'terms' / 'privacy' replace the matching
// static anchors inside the iframe, 'other' is a plain footer entry.
export type FooterLinkType = 'terms' | 'privacy' | 'other';

// Partner footer link the iframe shows in the My Packs / Delivery footers.
export interface FooterLink {
  label: string;
  url: string;
  type: FooterLinkType;
}

/** Partner ad pixels the game iframe loads for this operator's players. Only
 *  the providers that are set come back; `consent` is stamped by the backend
 *  from whoever ticked the box. */
export interface TrackingPixels {
  meta?: { pixel_id: string };
  reddit?: { pixel_id: string };
  google_ads?: { conversion_id: string; purchase_label: string; checkout_label?: string };
  ga4?: { measurement_id: string };
  report_currency?: string;
  consent?: { accepted_by: string; accepted_at: string };
}

/** What the page sends: the backend never takes consent from the payload. */
export type TrackingPixelsInput = Omit<TrackingPixels, 'consent'>;

/** One downloadable original of the Media Kit. `id` is the attachment id the
 *  download endpoint takes; width/height stay null until the blob is analyzed. */
export interface MediaKitAsset {
  id: number;
  kind: 'photo' | 'slab' | 'pack_art';
  label: string;
  preview_url: string;
  filename: string;
  content_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
}

/** A top card of a pack: photos in attachment order, then the slab scan. */
export interface MediaKitCard {
  id: number;
  title: string;
  /** USD. */
  value: number;
  tier: string | null;
  assets: MediaKitAsset[];
}

export interface MediaKitPack {
  id: number;
  title: string;
  price: number;
  pack_art: MediaKitAsset | null;
}

export interface MediaKitPackResponse {
  pack: MediaKitPack;
  /** FMV desc, at most 50. */
  cards: MediaKitCard[];
  meta: { total_count: number };
}

/** A short-lived presigned URL with attachment disposition: the browser goes
 *  there itself, the bytes never pass through the console. */
export interface MediaKitDownload {
  url: string;
  filename: string;
  expires_in: number;
}

export interface MediaKitArchive {
  token: string;
  status: 'pending' | 'ready' | 'failed';
  /** Present once ready. */
  url: string | null;
  filename: string;
  expires_at: string | null;
}

/** Launch PIECES, not a URL: the console composes the launch URL itself from
 *  VITE_IFRAME_ORIGIN (see lib/previewLaunchUrl) — the backend has no iframe
 *  base configured. `game_id` is the deep-linked pack, null for the lobby. */
export interface IframeUrlResponse {
  session_token: string;
  game_id: number | null;
  expires_at: string;
  template: string;
  currency?: string;
}

export interface RotateSecretResponse {
  api_secret: string;
  rotated_at: string;
  one_time: boolean;
}

export type CallbackAction =
  | 'get_session'
  | 'get_balance'
  | 'debit'
  | 'credit'
  | 'refund'
  | 'shipping_debit'
  | 'shipping_settle';

export interface CallbackLogSummary {
  id: number;
  action: CallbackAction | string;
  request_method: string;
  request_url: string;
  response_status: number | null;
  success: boolean;
  duration_ms: number | null;
  external_transaction_id: string | null;
  round_id: string | null;
  resent_from_callback_log_id: number | null;
  can_resend: boolean;
  created_at: string;
}

export interface CallbackLogDetail extends CallbackLogSummary {
  request_headers: Record<string, string>;
  // Object when parsed; a string when the backend truncated an oversized body.
  request_body: Record<string, unknown> | string | null;
  response_headers: Record<string, string>;
  response_body: string | null;
  error_class: string | null;
  error_message: string | null;
  session_id: number | null;
  transaction_id: number | null;
}

export interface CallbackLogsResponse {
  callback_logs: CallbackLogSummary[];
  meta: PaginationMeta;
}

export interface ListCallbackLogsParams {
  page?: number;
  per?: number;
  action_filter?: CallbackAction | string;
  success?: 'true' | 'false';
  session_id?: number;
  external_transaction_id?: string;
}

// A player account of this operator whose activity is excluded from reporting
// and from settlement, for its whole history.
export type TestPlayerStatus = 'active' | 'inactive';

// Whether the id matched a real session when it was added. `unknown` matched
// nothing — most likely a typo, and a typo excludes nobody while looking like
// it did. `case_corrected` was rewritten to the casing found in sessions.
export type TestPlayerMatchStatus = 'unknown' | 'exact' | 'case_corrected';

export type TestPlayerSource =
  | 'admin_ui'
  | 'admin_csv'
  | 'operator_api'
  | 'operator_csv'
  | 'system';

// Who placed the mark. `operator` rows are the partner's own and the only ones
// it may lift; `admin` and `system` rows are ours — visible, but locked.
export type TestPlayerAddedBy = 'admin' | 'operator' | 'system';

export interface TestPlayer {
  id: number;
  account_id: string;
  status: TestPlayerStatus | string;
  match_status: TestPlayerMatchStatus | string;
  source: TestPlayerSource | string;
  added_by: TestPlayerAddedBy | string;
  // Sent only on the operator's own rows. On ours the key is left out of the
  // payload entirely, because the note behind the mark is internal — so
  // `undefined` means "withheld" and `null` means "the operator gave none".
  reason?: string | null;
  created_at: string;
  deactivated_at: string | null;
}

export interface TestPlayersResponse {
  test_players: TestPlayer[];
  meta: PaginationMeta;
}

export type TestPlayersStatusFilter = 'active' | 'inactive' | 'all';

export type TestPlayerAddStatus =
  | 'added'
  | 'reactivated'
  | 'already_present'
  | 'invalid';

export interface TestPlayerAddResult {
  submitted: string;
  // null on an `invalid` entry — nothing survived normalization.
  account_id: string | null;
  status: TestPlayerAddStatus | string;
  match?: TestPlayerMatchStatus | string | null;
  // Session ids differing from the submitted one only by case, returned when
  // the backend found several and could not pick a canonical one.
  case_variants?: string[] | null;
  error?: string | null;
  message?: string | null;
}

export interface TestPlayersAddSummary {
  added: number;
  reactivated: number;
  already_present: number;
  invalid?: number;
  unknown_account?: number;
}

export interface TestPlayersAddResponse {
  summary: TestPlayersAddSummary;
  results: TestPlayerAddResult[];
}

export type TestPlayerDeactivateStatus =
  | 'deactivated'
  | 'already_inactive'
  | 'not_present'
  // The mark was placed by us, so the operator console may not lift it.
  | 'forbidden'
  // Nothing usable survived normalization of the submitted string.
  | 'invalid';

export interface TestPlayerDeactivateResult {
  submitted: string;
  // Missing on an `invalid` row — no canonical id was derived from the input.
  account_id?: string | null;
  status: TestPlayerDeactivateStatus | string;
  // Machine-readable refusal, e.g. `not_added_by_operator`.
  error?: string | null;
  // The backend's own wording for a refused or unreadable row. It is the only
  // place that knows why, so the screen shows it verbatim.
  message?: string | null;
}

// Counts are optional throughout: `results` carries the per-id truth and the
// screen recomputes from it whatever the summary leaves out.
export interface TestPlayersDeactivateSummary {
  deactivated?: number;
  already_inactive?: number;
  not_present?: number;
  forbidden?: number;
  invalid?: number;
}

export interface TestPlayersDeactivateResponse {
  summary: TestPlayersDeactivateSummary;
  results: TestPlayerDeactivateResult[];
}

export interface SecurityState {
  current_ip: string;
  is_owner: boolean;
  /** Mine: the addresses I sign in from and whether I use an authenticator.
   *  Everyone on the team has these. */
  personal: {
    console_ip_whitelist: string[];
    two_factor_enrolled: boolean;
  };
  /** The perimeter for the whole console. Present for the owner only. */
  operator?: {
    console_ip_whitelist: string[];
    require_team_2fa: boolean;
  };
}

// A card delivery ordered by one of this operator's players. Read-only: status,
// tracking and refunds are Repackz-side, so there is no update path.
export type ShippingStatus =
  | 'pending'
  | 'waiting_for_payment'
  | 'paid'
  | 'shipped'
  | 'delivered'
  | 'canceled'
  | 'merged';

export type ShippingType = 'delivery' | 'smc_transfer';

// What the player paid at checkout, in `currency` (the operator's own at that
// moment). null on shipments made before the charge snapshot existed.
export interface ShippingFees {
  shipping: number | null;
  insurance: number | null;
  signature: number | null;
  handling: number | null;
  sales_tax: number | null;
  total: number | null;
  // Set when an operator-funded promotion priced this delivery: the fields
  // above are then the promotional price, and this block the regular price and
  // what the operator covered. Optional while the backend rolls out.
  promo?: ShippingFeesPromo | null;
}

export interface ShippingFeesPromo {
  id: number;
  label: string;
  original_shipping: number;
  original_handling: number;
  covers?: ShippingPromotionFee[] | null;
  original_insurance?: number | null;
  original_signature?: number | null;
  discount: number;
}

export interface ShippingAddressSummary {
  id: number;
  name: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  country: string | null;
}

export interface ShippingAddress extends ShippingAddressSummary {
  first_line: string | null;
  second_line: string | null;
  apartment_number: string | null;
  phone_number: string | null;
}

// Insurance / signature the shipment required vs. what the player took. An
// `opted_out` shipment travelled uninsured on the player's own acknowledgement.
export interface ShippingProtection {
  required: Record<string, unknown> | null;
  chosen: Record<string, unknown> | null;
  opted_out: boolean | null;
  opted_out_of: string[];
  acknowledged: boolean | null;
  single_card_over_max: boolean | null;
  insurance_capped: boolean | null;
}

// `unavailable` is a card the warehouse could not ship: the player got the
// money back, so the line closed without a delivery.
export type ShippingCardSettleStatus = 'pending' | 'shipped' | 'unavailable';

export interface ShippingCard {
  // The delivery line, not the card: the same card can travel again later, and
  // this is what the "Can't ship" call addresses.
  id: number;
  // Whether this card may be reported unfulfillable right now. The backend owns
  // the rule (game-platform card, delivery still in custody, line unresolved);
  // the page only obeys the flag.
  can_report_unavailable: boolean;
  pool_item_id: number;
  name: string | null;
  grading_company: string | null;
  grade: string | null;
  certificate_number: string | null;
  // Value locked when the card was won — always USD cents, whatever the
  // operator's own currency is.
  locked_fmv_usd_cents: number | null;
  round_id: number | null;
  pack_title: string | null;
  settle_status: ShippingCardSettleStatus | string;
  /** Whose stock this card came from. Two answers only: yours, or shared —
   *  a card owned by another operator reads as shared, and their name is
   *  never sent. Optional while the backend rolls out. */
  owner?: { mine: boolean; name: string | null };
  refund: { amount_cents: number | null; amount_usd_cents: number | null; at: string | null } | null;
}

export interface PoolShipping {
  id: number;
  created_at: string;
  status: ShippingStatus | string;
  humanized_status: string;
  status_updated_at: string | null;
  direction: string;
  shipping_type: ShippingType | string;
  ship_my_cards_nickname: string | null;
  source: { id: number; name: string; code: string };
  user_id: number;
  // The player id as the operator knows it; null on deliveries whose round
  // predates the outcome log.
  operator_account_id: string | null;
  cards_count: number;
  // Locked FMV of all the delivery's cards together — always USD cents; null
  // on deliveries that predate the outcome log.
  locked_fmv_usd_cents: number | null;
  currency: string | null;
  total_amount: string | number | null;
  fees: ShippingFees | null;
  // Only present once shipped — before that there is nothing to track.
  tracking_number: string | null;
  tracking_carrier: string | null;
  address: ShippingAddressSummary | null;
}

export type SandboxActionName = 'mark_shipped' | 'mark_delivered' | 'cancel';

export interface SandboxControl {
  available: boolean;
  // Why the delivery refuses the action right now; null when it is available.
  reason: string | null;
}

// Staging only: the test transitions the delivery allows right now. The
// backend never sends it in production, and the page renders nothing then.
export type ShippingSandbox = Record<SandboxActionName, SandboxControl>;

export interface SandboxShipParams {
  company_name: string;
  tracking_number: string;
}

export interface PoolShippingDetail extends PoolShipping {
  address: ShippingAddress | null;
  protection: ShippingProtection | null;
  cards: ShippingCard[];
  refunded_count: number;
  // 'refunded' / 'partially_shipped' once the outcome is settled; null while
  // the shipment is still being worked.
  settle_summary: string | null;
  sandbox?: ShippingSandbox;
}

export interface PoolShippingsResponse {
  pool_shippings: PoolShipping[];
  meta: PaginationMeta & { sandbox?: boolean };
}

export interface ListShippingsParams {
  page?: number;
  per?: number;
  status?: ShippingStatus | string;
  shipping_type?: ShippingType | string;
  /** Exact delivery id. */
  id?: number;
  /** Exact certificate of a card the delivery carries. */
  certificate_number?: string;
  user_id?: number;
  user_email?: string;
  tracking_number?: string;
  /** Inclusive calendar dates (YYYY-MM-DD) on the created_at column. */
  created_from?: string;
  created_to?: string;
}

/** The export takes the list's filters but no paging: it returns every match. */
export type ExportShippingsParams = Omit<ListShippingsParams, 'page' | 'per'>;

export type ShippingPromotionKind = 'fixed_price' | 'percent_off' | 'amount_off';

/** Which fees the promotional price replaces; the rest stay at the regular price. */
/** One part of a delivery's price a promotion can cover. `shipping` is the
 *  carrier rate alone; `insurance` and `signature` are the add-ons. */
export type ShippingPromotionFee = 'shipping' | 'insurance' | 'signature' | 'handling';

export type ShippingPromotionStatus = 'scheduled' | 'active' | 'ended' | 'disabled';

/** A delivery price the operator funds for a window. `amount` and
 *  `max_discount` are in `meta.currency` units, not cents; `amount` is the price
 *  the player pays (`fixed_price`) or the cut (`amount_off`), null for
 *  `percent_off`. Times are ISO 8601 UTC. */
export interface ShippingPromotion {
  id: number;
  label: string;
  kind: ShippingPromotionKind;
  amount: number | null;
  percent_off: number | null;
  covers: ShippingPromotionFee[];
  max_discount: number | null;
  starts_at: string;
  ends_at: string;
  disabled_at: string | null;
  status: ShippingPromotionStatus;
  /** Scheduled or active: the only ones that can still be changed. */
  editable: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** The body of create and update. Fields the kind does not use go as null, so
 *  an edit that changes the kind clears the old value. */
export interface ShippingPromotionInput {
  label: string;
  kind: ShippingPromotionKind;
  amount: number | null;
  percent_off: number | null;
  covers: ShippingPromotionFee[];
  max_discount: number | null;
  starts_at: string;
  ends_at: string;
}

export interface ShippingPromotionsResponse {
  shipping_promotions: ShippingPromotion[];
  meta: {
    /** False when the operator does not qualify for promotions yet. */
    eligible: boolean;
    eligibility_message: string | null;
    currency: string;
  };
}

// The list and its CSV export filter on the same params. Reads only the filter
// keys, so paging never leaks into the export.
function appendShippingFilters(
  query: URLSearchParams,
  params: ExportShippingsParams,
): URLSearchParams {
  if (params.status) query.set('status', params.status);
  if (params.shipping_type) query.set('shipping_type', params.shipping_type);
  if (params.id !== undefined) query.set('id', String(params.id));
  if (params.certificate_number) query.set('certificate_number', params.certificate_number);
  if (params.user_id !== undefined) query.set('user_id', String(params.user_id));
  if (params.user_email) query.set('user_email', params.user_email);
  if (params.tracking_number) query.set('tracking_number', params.tracking_number);
  if (params.created_from) query.set('created_from', params.created_from);
  if (params.created_to) query.set('created_to', params.created_to);
  return query;
}

export const operatorConsole = {
  me: async () => {
    const res = await apiRequest<OperatorMe>(`${BASE}/me`);
    return res.body;
  },

  getSettings: async () => {
    const res = await apiRequest<{ settings: OperatorSettings }>(`${BASE}/settings`);
    return res.body.settings;
  },

  patchSettings: async (patch: Partial<OperatorSettings>) => {
    const res = await apiRequest<{ settings: OperatorSettings }>(`${BASE}/settings`, {
      method: 'PATCH',
      body: { settings: patch },
    });
    return res.body.settings;
  },

  listTemplateThemes: async () => {
    const res = await apiRequest<{ template_themes: TemplateThemeMeta[] }>(
      `${BASE}/template_themes`,
    );
    return res.body;
  },

  getTemplateTheme: async (template: string) => {
    const res = await apiRequest<{ template_theme: TemplateTheme }>(
      `${BASE}/template_themes/${template}`,
    );
    return res.body.template_theme;
  },

  putTemplateTheme: async (template: string, design_system: Record<string, unknown>) => {
    const res = await apiRequest<{ template_theme: TemplateTheme }>(
      `${BASE}/template_themes/${template}`,
      {
        method: 'PUT',
        body: { template_theme: { design_system } },
      },
    );
    return res.body.template_theme;
  },

  // 404 not_found when the operator lacks allow_packs_view — the backend
  // hides the feature's existence; surfaces as ApiError like any non-2xx.
  listPacks: async () => {
    const res = await apiRequest<{ packs: Pack[] }>(`${BASE}/packs`);
    return res.body;
  },

  // includeStaged widens availableOnly (live packs) to live + staged, so the
  // Configs page can dress a pack before it is published. Only meaningful
  // together with availableOnly.
  listPoolOfferConfigs: async (opts: { availableOnly?: boolean; includeStaged?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.availableOnly) params.set('available_only', 'true');
    if (opts.includeStaged) params.set('include_staged', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await apiRequest<{ pool_offer_configs: PoolOfferConfig[] }>(
      `${BASE}/pool_offer_configs${qs}`,
    );
    return res.body;
  },

  // Publish (staged -> enabled) / unpublish (enabled -> staged) one pack. The
  // backend only allows moves between those two states — everything else is
  // our opt-in gate or machine state and answers 403.
  setPoolOfferAvailability: async (poolOfferId: number, availability: 'enabled' | 'staged') => {
    const res = await apiRequest<{ pool_offer_config: PoolOfferConfig }>(
      `${BASE}/pool_offer_configs/${poolOfferId}`,
      { method: 'PATCH', body: { pool_offer_config: { availability } } },
    );
    return res.body.pool_offer_config;
  },

  patchPoolOfferConfig: async (poolOfferId: number, patch: Partial<PoolOfferConfig>) => {
    const res = await apiRequest<{ pool_offer_config: PoolOfferConfig }>(
      `${BASE}/pool_offer_configs/${poolOfferId}`,
      { method: 'PATCH', body: { pool_offer_config: patch } },
    );
    return res.body.pool_offer_config;
  },

  // Applies RTP / buyback to every pack for this operator in one backend
  // transaction. Both fields are percentages (e.g. rtp: 96, buyback: 90) — the
  // backend divides by 100. Only include the fields the operator filled in;
  // an omitted field is left untouched. Returns the full, refreshed list.
  //
  // Send the SAME filters the grid was loaded with: the backend prices exactly
  // the packs it lists back, so calling this unfiltered would both overwrite
  // pricing on packs the operator never enabled and hand us a superset that
  // replaces the grid.
  applyGlobalPricing: async (params: {
    rtp?: number;
    buyback?: number;
    availableOnly?: boolean;
    includeStaged?: boolean;
    /** Only these packs; omitted = every pack the operator can price. */
    poolOfferIds?: number[];
  }) => {
    const body: {
      rtp?: number;
      buyback?: number;
      available_only?: boolean;
      include_staged?: boolean;
      pool_offer_ids?: number[];
    } = {};
    if (params.rtp !== undefined) body.rtp = params.rtp;
    if (params.buyback !== undefined) body.buyback = params.buyback;
    if (params.availableOnly) body.available_only = true;
    if (params.includeStaged) body.include_staged = true;
    if (params.poolOfferIds) body.pool_offer_ids = params.poolOfferIds;
    const res = await apiRequest<{ pool_offer_configs: PoolOfferConfig[]; rtp_skipped?: RtpSkippedPack[] }>(
      `${BASE}/pool_offer_configs/apply_global_pricing`,
      { method: 'POST', body },
    );
    return res.body;
  },

  listPoolCategoryConfigs: async () => {
    const res = await apiRequest<{ pool_category_configs: PoolCategoryConfig[] }>(
      `${BASE}/pool_category_configs`,
    );
    return res.body;
  },

  patchPoolCategoryConfig: async (
    poolCategoryId: number,
    patch: Partial<PoolCategoryConfig>,
  ) => {
    const res = await apiRequest<{ pool_category_config: PoolCategoryConfig }>(
      `${BASE}/pool_category_configs/${poolCategoryId}`,
      { method: 'PATCH', body: { pool_category_config: patch } },
    );
    return res.body.pool_category_config;
  },

  uploadPoolCategoryConfigImage: async (poolCategoryId: number, file: File) => {
    const fd = new FormData();
    fd.append('pool_category_config[image]', file);
    const res = await apiRequest<{ pool_category_config: PoolCategoryConfig }>(
      `${BASE}/pool_category_configs/${poolCategoryId}`,
      { method: 'PATCH', body: fd },
    );
    return res.body.pool_category_config;
  },

  deletePoolCategoryConfigImage: async (poolCategoryId: number) => {
    const res = await apiRequest<{ pool_category_config: PoolCategoryConfig }>(
      `${BASE}/pool_category_configs/${poolCategoryId}/image`,
      { method: 'DELETE' },
    );
    return res.body.pool_category_config;
  },

  getFooterLinks: async () => {
    const res = await apiRequest<{ footer_links: FooterLink[] }>(`${BASE}/footer_links`);
    return res.body.footer_links;
  },

  // Full-list replace — the page edits the list as a unit, mirroring the
  // backend PUT semantics (no per-row ids).
  putFooterLinks: async (links: FooterLink[]) => {
    const res = await apiRequest<{ footer_links: FooterLink[] }>(`${BASE}/footer_links`, {
      method: 'PUT',
      body: { footer_links: links },
    });
    return res.body.footer_links;
  },

  getTrackingPixels: async () => {
    const res = await apiRequest<{ tracking_pixels: TrackingPixels }>(`${BASE}/tracking_pixels`);
    return res.body.tracking_pixels;
  },

  // Full replace, like footer links. Consent is stamped server-side from the
  // signed-in user; the page only says whether the box was ticked.
  putTrackingPixels: async (config: TrackingPixelsInput, consentAccepted: boolean) => {
    const res = await apiRequest<{ tracking_pixels: TrackingPixels }>(`${BASE}/tracking_pixels`, {
      method: 'PUT',
      body: { tracking_pixels: config, consent_accepted: consentAccepted },
    });
    return res.body.tracking_pixels;
  },

  listSessions: async (params: { page?: number; per?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    const qs = query.toString();
    const res = await apiRequest<SessionsResponse>(
      `${BASE}/sessions${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  listTransactions: async (
    params: { session_id?: number; page?: number; per?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.session_id !== undefined) query.set('session_id', String(params.session_id));
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    const qs = query.toString();
    const res = await apiRequest<TransactionsResponse>(
      `${BASE}/transactions${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  rotateSecret: async () => {
    const res = await apiRequest<RotateSecretResponse>(
      `${BASE}/api_keys/rotate_secret`,
      { method: 'POST', body: {} },
    );
    return res.body;
  },

  iframeUrl: async (params: {
    template: string;
    player_id?: string;
    disable_shipping?: boolean;
    currency?: string;
    lobby_url?: string;
    deposit_url?: string;
  }) => {
    const res = await apiRequest<IframeUrlResponse>(`${BASE}/iframe_url`, {
      method: 'POST',
      body: params,
    });
    return res.body;
  },

  uploadPoolOfferConfigImage: async (poolOfferId: number, slot: string, file: File) => {
    const fd = new FormData();
    fd.append(`pool_offer_config[${slot}]`, file);
    const res = await apiRequest<{ pool_offer_config: PoolOfferConfig }>(
      `${BASE}/pool_offer_configs/${poolOfferId}`,
      { method: 'PATCH', body: fd },
    );
    return res.body.pool_offer_config;
  },

  deletePoolOfferConfigImage: async (poolOfferId: number, slot: string) => {
    const res = await apiRequest<{ pool_offer_config: PoolOfferConfig }>(
      `${BASE}/pool_offer_configs/${poolOfferId}/images/${slot}`,
      { method: 'DELETE' },
    );
    return res.body.pool_offer_config;
  },

  listCallbackLogs: async (params: ListCallbackLogsParams = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    // Type filter is `action_filter` (NOT `action`) per the backend contract.
    if (params.action_filter) query.set('action_filter', params.action_filter);
    if (params.success !== undefined) query.set('success', params.success);
    if (params.session_id !== undefined) {
      query.set('session_id', String(params.session_id));
    }
    if (params.external_transaction_id) {
      query.set('external_transaction_id', params.external_transaction_id);
    }
    const qs = query.toString();
    const res = await apiRequest<CallbackLogsResponse>(
      `${BASE}/callback_logs${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  getCallbackLog: async (id: number) => {
    const res = await apiRequest<{ callback_log: CallbackLogDetail }>(
      `${BASE}/callback_logs/${id}`,
    );
    return res.body.callback_log;
  },

  // Replays the callback. A 403 (operator may not resend this one) surfaces as
  // an ApiError, exactly like every other non-2xx; the caller reads its message.
  resendCallbackLog: async (id: number) => {
    const res = await apiRequest<{ callback_log: CallbackLogDetail }>(
      `${BASE}/callback_logs/${id}/resend`,
      { method: 'POST', body: {} },
    );
    return res.body.callback_log;
  },

  // Readable by every operator — the registry drives their settlement figures,
  // so they see it even when they may not edit it.
  listTestPlayers: async (
    params: {
      status?: TestPlayersStatusFilter;
      /** Case-insensitive substring of the player id. */
      search?: string;
      page?: number;
      per?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    const qs = query.toString();
    const res = await apiRequest<TestPlayersResponse>(
      `${BASE}/test_players${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  // Send `account_ids` for an already-split list, or `csv` for a raw paste —
  // the backend splits it on newline / comma / semicolon and also reads a CSV
  // with an `account_id` column. Idempotent: re-sending an id that is already
  // registered reports `already_present` instead of duplicating it.
  // 403 test_player_registry_disabled when the operator may not edit.
  addTestPlayers: async (params: {
    account_ids?: string[];
    csv?: string;
    reason?: string;
  }) => {
    const body: Record<string, unknown> = {};
    if (params.account_ids) body.account_ids = params.account_ids;
    if (params.csv !== undefined) body.csv = params.csv;
    if (params.reason) body.reason = params.reason;
    const res = await apiRequest<TestPlayersAddResponse>(`${BASE}/test_players`, {
      method: 'POST',
      body,
    });
    return res.body;
  },

  // Bulk path: ids travel in the body, so casino ids with slashes or dots need
  // no URL encoding. Answers 200 even when it lifted nothing: rows we placed
  // come back as `forbidden`, so the caller has to read `results` rather than
  // treat the absence of an exception as success.
  deactivateTestPlayers: async (accountIds: string[], reason?: string) => {
    const body: Record<string, unknown> = { account_ids: accountIds };
    if (reason) body.reason = reason;
    const res = await apiRequest<TestPlayersDeactivateResponse>(
      `${BASE}/test_players/bulk_deactivate`,
      { method: 'POST', body },
    );
    return res.body;
  },

  // Single-row deactivation, same envelope as the bulk path. The record is
  // never destroyed — the backend only stamps deactivated_at, so re-adding the
  // id revives the same row. Throws ApiError(403) when nothing could be
  // lifted, which happens both when the registry gate is closed and when the
  // mark is one of ours.
  deleteTestPlayer: async (accountId: string) => {
    const res = await apiRequest<TestPlayersDeactivateResponse>(
      `${BASE}/test_players/${encodeURIComponent(accountId)}`,
      { method: 'DELETE' },
    );
    return res.body;
  },

  // Deliveries of this operator's own players, newest first. Outbound only —
  // inbound warehouse intake is not the operator's shipment.
  listShippings: async (params: ListShippingsParams = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    const qs = appendShippingFilters(query, params).toString();
    const res = await apiRequest<PoolShippingsResponse>(
      `${BASE}/pool_shippings${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  // Every delivery matching the filters as one CSV file (streamed, newest
  // first) — the reconciliation copy of the list, without its paging.
  exportShippings: async (params: ExportShippingsParams = {}) => {
    const qs = appendShippingFilters(new URLSearchParams(), params).toString();
    return apiDownload(`${BASE}/pool_shippings/export${qs ? `?${qs}` : ''}`);
  },

  // 404 both for an id that does not exist and for another operator's
  // delivery — the endpoint never confirms that someone else's id is real.
  getShipping: async (id: number) => {
    const res = await apiRequest<{ pool_shipping: PoolShippingDetail }>(
      `${BASE}/pool_shippings/${id}`,
    );
    return res.body.pool_shipping;
  },

  // "🚫 Can't ship": one card of the delivery cannot be fulfilled. The player
  // is bought out at 100% of the higher of the card's fair market value or the
  // pack price and its handling is refunded — the rest of the delivery keeps
  // travelling. Not reversible, so the page confirms first. Answers the whole
  // refreshed delivery; 422 when the card left the reportable window.
  reportCardUnavailable: async (shippingId: number, cardId: number) => {
    const res = await apiRequest<{ pool_shipping: PoolShippingDetail }>(
      `${BASE}/pool_shippings/${shippingId}/items/${cardId}/cannot_ship`,
      { method: 'POST', body: {} },
    );
    return res.body.pool_shipping;
  },

  // Staging sandbox (404 in production): the partner moves a test delivery
  // through the real transitions and receives the real webhooks and
  // callbacks. Each answers the refreshed delivery; 422 names the refusal.
  sandboxShipShipping: async (id: number, params: SandboxShipParams) => {
    const res = await apiRequest<{ pool_shipping: PoolShippingDetail }>(
      `${BASE}/pool_shippings/${id}/sandbox/ship`,
      { method: 'POST', body: params },
    );
    return res.body.pool_shipping;
  },

  sandboxDeliverShipping: async (id: number) => {
    const res = await apiRequest<{ pool_shipping: PoolShippingDetail }>(
      `${BASE}/pool_shippings/${id}/sandbox/deliver`,
      { method: 'POST', body: {} },
    );
    return res.body.pool_shipping;
  },

  sandboxCancelShipping: async (id: number) => {
    const res = await apiRequest<{ pool_shipping: PoolShippingDetail }>(
      `${BASE}/pool_shippings/${id}/sandbox/cancel`,
      { method: 'POST', body: {} },
    );
    return res.body.pool_shipping;
  },

  // Shipping promotions, newest start first. Every route answers 404 while the
  // operator lacks allow_shipping_promotions. Writes answer 422 `errors` for a
  // rule that does not hold (overlap, operator not eligible, an ended promotion).
  listShippingPromotions: async () => {
    const res = await apiRequest<ShippingPromotionsResponse>(`${BASE}/shipping_promotions`);
    return res.body;
  },

  createShippingPromotion: async (input: ShippingPromotionInput) => {
    const res = await apiRequest<{ shipping_promotion: ShippingPromotion }>(
      `${BASE}/shipping_promotions`,
      { method: 'POST', body: { shipping_promotion: input } },
    );
    return res.body.shipping_promotion;
  },

  updateShippingPromotion: async (id: number, input: ShippingPromotionInput) => {
    const res = await apiRequest<{ shipping_promotion: ShippingPromotion }>(
      `${BASE}/shipping_promotions/${id}`,
      { method: 'PATCH', body: { shipping_promotion: input } },
    );
    return res.body.shipping_promotion;
  },

  // Ends the promotion for good: deliveries created from now on are priced
  // regularly, and a disabled promotion cannot be turned back on.
  disableShippingPromotion: async (id: number) => {
    const res = await apiRequest<{ shipping_promotion: ShippingPromotion }>(
      `${BASE}/shipping_promotions/${id}/disable`,
      { method: 'POST' },
    );
    return res.body.shipping_promotion;
  },

  getSecurity: async () => {
    const res = await apiRequest<SecurityState>(`${BASE}/security`);
    return res.body;
  },

  // The operator-wide perimeter. Owner only, and it may never be emptied
  // from here — clearing it is a support action.
  updateIpWhitelist: async (ip_whitelist_text: string, current_password: string) => {
    const res = await apiRequest<SecurityState>(`${BASE}/security/ip_whitelist`, {
      method: 'PATCH',
      body: { ip_whitelist_text, current_password },
    });
    return res.body;
  },

  // My own addresses. Anyone on the team may set these, and an empty list is
  // allowed: it means "I place no restriction of my own".
  updatePersonalIpWhitelist: async (ip_whitelist_text: string, current_password: string) => {
    const res = await apiRequest<SecurityState>(`${BASE}/security/personal_ip_whitelist`, {
      method: 'PATCH',
      body: { ip_whitelist_text, current_password },
    });
    return res.body;
  },

  requireTeam2fa: async (current_password: string) => {
    const res = await apiRequest<SecurityState>(`${BASE}/security/require_team_2fa`, {
      method: 'POST',
      body: { current_password },
    });
    return res.body;
  },

  enrollSecurityOtp: async (current_password: string) => {
    const res = await apiRequest<{ otpauth_uri: string; secret: string }>(
      `${BASE}/security/otp/enroll`,
      { method: 'POST', body: { current_password } },
    );
    return res.body;
  },

  confirmSecurityOtp: async (code: string) => {
    const res = await apiRequest<{ recovery_codes: string[] }>(
      `${BASE}/security/otp/confirm`,
      { method: 'POST', body: { code } },
    );
    return res.body;
  },

  // KPI overview. 404 when the operator has no allow_metrics — the backend
  // hides the feature rather than admitting it exists, so the screen treats it
  // like an unknown URL. 422 INVALID_TIME_INTERVAL for a bad or too-wide window.
  getMetrics: async (params: GetMetricsParams = {}) => {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.granularity) query.set('granularity', params.granularity);
    if (params.cohort_granularity) query.set('cohort_granularity', params.cohort_granularity);
    if (params.cohort_timeframe) query.set('cohort_timeframe', String(params.cohort_timeframe));
    const qs = query.toString();

    const res = await apiRequest<OperatorMetricsResponse>(
      `${BASE}/metrics${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  // Team management. Owner-only on the backend, so every call here answers
  // 403 FORBIDDEN_SECTION for a member even if the screen were reachable.
  listTeam: async () => {
    const res = await apiRequest<TeamResponse>(`${BASE}/team`);
    return res.body;
  },

  addTeamMember: async (email: string, permissions: ConsoleSection[]) => {
    const res = await apiRequest<TeamMutationResponse>(`${BASE}/team`, {
      method: 'POST',
      body: { email, permissions },
    });
    return res.body;
  },

  updateTeamMember: async (
    id: number,
    patch: { permissions?: ConsoleSection[]; role?: ConsoleRole },
  ) => {
    const res = await apiRequest<TeamMutationResponse>(`${BASE}/team/${id}`, {
      method: 'PATCH',
      body: patch,
    });
    return res.body;
  },

  removeTeamMember: async (id: number) => {
    await apiRequest(`${BASE}/team/${id}`, { method: 'DELETE' });
  },

  resetTeamMemberPassword: async (id: number) => {
    const res = await apiRequest<TeamMutationResponse>(`${BASE}/team/${id}/reset_password`, {
      method: 'POST',
    });
    return res.body;
  },

  // Webhooks. Grantable as its own section, so an owner can hand the wiring
  // to whoever integrates without handing over the rest of the console.
  listWebhookEndpoints: async () => {
    const res = await apiRequest<WebhookEndpointsResponse>(`${BASE}/webhook_endpoints`);
    return res.body;
  },

  createWebhookEndpoint: async (endpoint: {
    url: string;
    description?: string;
    event_types: string[];
  }) => {
    const res = await apiRequest<{ webhook_endpoint: WebhookEndpoint }>(
      `${BASE}/webhook_endpoints`,
      { method: 'POST', body: { webhook_endpoint: endpoint } },
    );
    return res.body.webhook_endpoint;
  },

  updateWebhookEndpoint: async (
    id: number,
    patch: {
      url?: string;
      description?: string;
      event_types?: string[];
      status?: 'active' | 'paused';
    },
  ) => {
    const res = await apiRequest<{ webhook_endpoint: WebhookEndpoint }>(
      `${BASE}/webhook_endpoints/${id}`,
      { method: 'PATCH', body: { webhook_endpoint: patch } },
    );
    return res.body.webhook_endpoint;
  },

  deleteWebhookEndpoint: async (id: number) => {
    await apiRequest(`${BASE}/webhook_endpoints/${id}`, { method: 'DELETE' });
  },

  /** Queues a `ping`. Works on an endpoint subscribed to nothing, and on a
   *  paused one. Poll the returned delivery for the answer. */
  testWebhookEndpoint: async (id: number) => {
    const res = await apiRequest<WebhookAttemptResponse>(
      `${BASE}/webhook_endpoints/${id}/test`,
      { method: 'POST' },
    );
    return res.body;
  },

  listWebhookDeliveries: async (params: ListWebhookDeliveriesParams = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.per !== undefined) query.set('per', String(params.per));
    if (params.endpoint_id !== undefined) query.set('endpoint_id', String(params.endpoint_id));
    if (params.event_type) query.set('event_type', params.event_type);
    if (params.status) query.set('status', params.status);
    const qs = query.toString();
    const res = await apiRequest<WebhookDeliveriesResponse>(
      `${BASE}/webhook_deliveries${qs ? `?${qs}` : ''}`,
    );
    return res.body;
  },

  getWebhookDelivery: async (id: number) => {
    const res = await apiRequest<{ webhook_delivery: WebhookDelivery }>(
      `${BASE}/webhook_deliveries/${id}`,
    );
    return res.body.webhook_delivery;
  },

  /** Always allowed: a notification moves no money, so replaying one is safe
   *  even when it already arrived. Queues a NEW delivery carrying the same
   *  event rather than rewriting the old one, so the record of what happened
   *  the first time survives. */
  resendWebhookDelivery: async (id: number) => {
    const res = await apiRequest<WebhookAttemptResponse>(
      `${BASE}/webhook_deliveries/${id}/resend`,
      { method: 'POST' },
    );
    return res.body;
  },

  // ---------------------------------------------------------------------------
  // Media Kit. Every route answers 404 not_found when the operator lacks
  // allow_media_kit (the backend hides the feature) and for a pack that is not
  // enabled for this operator; 403 without the media_kit section.
  // ---------------------------------------------------------------------------
  mediaKit: {
    listPacks: async () => {
      const res = await apiRequest<{ packs: MediaKitPack[] }>(`${BASE}/media_kit/packs`);
      return res.body.packs;
    },

    getPack: async (id: number) => {
      const res = await apiRequest<MediaKitPackResponse>(`${BASE}/media_kit/packs/${id}`);
      return res.body;
    },

    /** 404 ASSET_NOT_FOUND once the card has left the pack's window. */
    assetDownload: async (packId: number, assetId: number) => {
      const res = await apiRequest<MediaKitDownload>(
        `${BASE}/media_kit/packs/${packId}/assets/${assetId}/download`,
      );
      return res.body;
    },

    /** 202 with the archive; within an hour the same pack gets the existing
     *  one back (pending or ready) instead of a new build. */
    requestArchive: async (packId: number, signal?: AbortSignal) => {
      const res = await apiRequest<{ archive: MediaKitArchive }>(
        `${BASE}/media_kit/packs/${packId}/archive`,
        signal ? { method: 'POST', signal } : { method: 'POST' },
      );
      return res.body.archive;
    },

    getArchive: async (token: string, signal?: AbortSignal) => {
      const res = await apiRequest<{ archive: MediaKitArchive }>(
        `${BASE}/media_kit/archives/${encodeURIComponent(token)}`,
        signal ? { signal } : {},
      );
      return res.body.archive;
    },
  },

  // ---------------------------------------------------------------------------
  // Pack Builder (My Packs). Every route answers 404 not_found when the
  // operator lacks allow_pack_builder (the backend hides the feature) and 404
  // for a pack that belongs to another operator; 403 without the pack_builder
  // section; 409 PACK_PUBLISHED when price/RTP/groups/reset_groups are
  // edited on a live pack (owner config enabled OR restocking, CONTRACT §4.8);
  // on publish 422 VALIDATION_FAILED (the report itself is re-fetched through
  // validate(), because apiRequest keeps only the message), 422
  // NO_POOL_CATEGORY / PUBLISH_FAILED_AFTER_ACTUALIZE and 409
  // AVAILABILITY_TRANSITION_FORBIDDEN carry their message only (§4.20); 409
  // PACK_HAS_ORDERS on delete (§4.25).
  // ---------------------------------------------------------------------------
  packBuilder: {
    listPacks: async () => {
      const res = await apiRequest<{ packs: PackBuilderPack[]; limits: PackBuilderLimits }>(
        `${BASE}/pack_builder/packs`,
      );
      return res.body;
    },

    createPack: async (attrs: PackBuilderCreateAttrs) => {
      const res = await apiRequest<{ pack: PackBuilderPack }>(`${BASE}/pack_builder/packs`, {
        method: 'POST',
        body: { pack: attrs },
      });
      return res.body.pack;
    },

    getPack: async (id: number) => {
      const res = await apiRequest<{ pack: PackBuilderPack }>(`${BASE}/pack_builder/packs/${id}`);
      return res.body.pack;
    },

    /** `{ reset_groups: true }` (without `groups`) is how "Reset Price Buckets"
     *  is saved — the backend rebuilds the ladder (CONTRACT §4.9). */
    updatePack: async (id: number, patch: PackBuilderUpdatePatch) => {
      const res = await apiRequest<{ pack: PackBuilderPack }>(`${BASE}/pack_builder/packs/${id}`, {
        method: 'PATCH',
        body: { pack: patch },
      });
      return res.body.pack;
    },

    deletePack: async (id: number) => {
      await apiRequest(`${BASE}/pack_builder/packs/${id}`, { method: 'DELETE' });
    },

    uploadAsset: async (id: number, slot: PackBuilderAssetSlot, file: File) => {
      const fd = new FormData();
      fd.append(`pack[${slot}]`, file);
      const res = await apiRequest<{ pack: PackBuilderPack }>(
        `${BASE}/pack_builder/packs/${id}/assets`,
        { method: 'PATCH', body: fd },
      );
      return res.body.pack;
    },

    deleteAsset: async (id: number, slot: PackBuilderAssetSlot) => {
      await apiRequest(`${BASE}/pack_builder/packs/${id}/assets/${slot}`, { method: 'DELETE' });
    },

    /** Same contract as iframeUrl, but the session is flagged preview_staged
     *  so the iframe shows this (possibly unpublished) pack. */
    previewUrl: async (
      id: number,
      params: { player_id: string; template?: string; currency?: string },
    ) => {
      const res = await apiRequest<IframeUrlResponse>(
        `${BASE}/pack_builder/packs/${id}/preview_url`,
        { method: 'POST', body: params },
      );
      return res.body;
    },

    /** Pack CONTENTS, straight from Postgres: every member whatever its
     *  engagement_status, so a card a player pulled is still listed here.
     *  `meta.total_count` is the pack's items_count by construction. */
    listItems: async (
      id: number,
      params: { page: number; per: number; sort?: PackBuilderSort; q?: string },
      signal?: AbortSignal,
    ) => {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('per', String(params.per));
      if (params.sort) query.set('sort', params.sort);
      if (params.q) query.set('q', params.q);
      const res = await apiRequest<PackBuilderItemsResponse>(
        `${BASE}/pack_builder/packs/${id}/items?${query.toString()}`,
        signal ? { signal } : {},
      );
      return res.body;
    },

    addItems: async (id: number, ids: number[]) => {
      const res = await apiRequest<AddItemsResponse>(`${BASE}/pack_builder/packs/${id}/items`, {
        method: 'POST',
        body: { ids },
      });
      return res.body;
    },

    removeItems: async (id: number, ids: number[]) => {
      const res = await apiRequest<RemoveItemsResponse>(`${BASE}/pack_builder/packs/${id}/items`, {
        method: 'DELETE',
        body: { ids },
      });
      return res.body;
    },

    /** Pure preview: nothing is written. `groups` travel in the same
     *  [{from,to,floor}] shape as updatePack. */
    validate: async (id: number, overrides: PackValidateOverrides) => {
      const res = await apiRequest<{ validation: PackValidation }>(
        `${BASE}/pack_builder/packs/${id}/validate`,
        { method: 'POST', body: overrides },
      );
      return res.body.validation;
    },

    publish: async (id: number) => {
      const res = await apiRequest<{ pack: PackBuilderPack }>(
        `${BASE}/pack_builder/packs/${id}/publish`,
        { method: 'POST' },
      );
      return res.body.pack;
    },

    unpublish: async (id: number) => {
      const res = await apiRequest<{ pack: PackBuilderPack }>(
        `${BASE}/pack_builder/packs/${id}/unpublish`,
        { method: 'POST' },
      );
      return res.body.pack;
    },

    /** Array filters go out Rails-style (`sport[]=1&sport[]=2`). The optional
     *  signal lets the Items screen drop a stale request. */
    inventory: async (params: InventoryParams, signal?: AbortSignal) => {
      const query = new URLSearchParams();
      query.set('pack_id', String(params.pack_id));
      const scalars: Array<[string, string | number | boolean | undefined]> = [
        ['q', params.q],
        ['ownership', params.ownership],
        ['fmv_min', params.fmv_min],
        ['fmv_max', params.fmv_max],
        ['category_id', params.category_id],
        ['year_from', params.year_from],
        ['year_to', params.year_to],
        ['player', params.player],
        ['rookie', params.rookie],
        ['sort', params.sort],
        ['page', params.page],
        ['per', params.per],
        ['group_variations', params.group_variations],
      ];
      for (const [key, value] of scalars) {
        if (value !== undefined && value !== '') query.set(key, String(value));
      }
      const lists: Array<[string, Array<string | number> | undefined]> = [
        ['sport', params.sport],
        ['grader', params.grader],
        ['grade', params.grade],
        ['set', params.set],
        ['pokemon_rarity', params.pokemon_rarity],
        ['pokemon_set', params.pokemon_set],
      ];
      for (const [key, values] of lists) {
        for (const value of values ?? []) query.append(`${key}[]`, String(value));
      }
      const res = await apiRequest<InventoryResponse>(
        `${BASE}/pack_builder/inventory?${query.toString()}`,
        signal ? { signal } : {},
      );
      return res.body;
    },

    groupsTemplate: async (price: number) => {
      const res = await apiRequest<{ groups: PackBuilderTemplateGroup[] }>(
        `${BASE}/pack_builder/groups_template?price=${encodeURIComponent(String(price))}`,
      );
      return res.body.groups;
    },

    /** Every LootMachine::PoolCategory for the pack form (CONTRACT §4.12) —
     *  NOT listPoolCategoryConfigs, which only knows categories that already
     *  have an enabled pack. */
    listPoolCategories: async () => {
      const res = await apiRequest<{ pool_categories: PackBuilderPoolCategory[] }>(
        `${BASE}/pack_builder/pool_categories`,
      );
      return res.body;
    },
  },

  // ---------------------------------------------------------------------------
  // Pack buckets. 403 pack_buckets_disabled without allow_pack_buckets, 404 for
  // a pack the operator does not sell. update: 422 buckets_invalid with the
  // check in `details.validation`, 409 pack_busy while the pack is being
  // recalculated, 409 not_editable with `locked_reason`.
  // ---------------------------------------------------------------------------
  packBuckets: {
    get: async (poolOfferId: number) => {
      const res = await apiRequest<{ buckets: PackBuckets }>(`${BASE}/pool_offers/${poolOfferId}/buckets`);
      return res.body.buckets;
    },

    /** Dry run: nothing is written. */
    validate: async (poolOfferId: number, groups: PackBucketRow[]) => {
      const res = await apiRequest<{ validation: PackBucketsCheck }>(
        `${BASE}/pool_offers/${poolOfferId}/buckets/validate`,
        { method: 'POST', body: { groups } },
      );
      return res.body.validation;
    },

    update: async (poolOfferId: number, groups: PackBucketRow[]) => {
      const res = await apiRequest<{ buckets: PackBuckets; validation: PackBucketsCheck }>(
        `${BASE}/pool_offers/${poolOfferId}/buckets`,
        { method: 'PATCH', body: { groups } },
      );
      return res.body;
    },
  },
};
